/**
 * Embed configuration — the wire format for a single-chart iframe embed.
 *
 * One module, two consumers: the "Einbetten" dialog ENCODES the chart's current
 * view into a query string, and the embed page (served from /embed/budget/:chart)
 * DECODES it back out of its own `window.location.search`. Because the same
 * bundle runs in both places there is exactly one implementation and no drift.
 *
 * Why explicit query params rather than an opaque base64/compressed blob:
 *
 *   - The names are the SAME ones the API already takes (see api/client.ts and
 *     apps/api/src/budget/schemas.ts), so an embed URL is readable in a server
 *     log and hand-editable by whoever pasted it.
 *   - Adding a filter later is an additive optional param — no version
 *     negotiation, no re-encoding of URLs already published on other sites.
 *   - A blob would also have to be versioned, and a server-stored short id
 *     would make every published embed depend on a row that must never be lost.
 *
 * Deliberately NOT supported: the free-text search (`q`). The API strips it —
 * BudgetQuerySchema has no `q` field and validateQuery replaces req.query with
 * the parsed object — so it never narrows chart data, while filterInfo.ts would
 * still print `Suche: "…"` into the subtitle. Encoding it would publish a chart
 * whose caption claims a filter that was never applied.
 */

import { SPOTLIGHTS } from '../components/Spotlights';
import type { DisplayUnit, FilterState } from '../types';

export type EmbedChartKind = 'area' | 'bar';

export interface EmbedConfig {
  chart: EmbedChartKind;
  filters: FilterState;
  /** null = follow the newest year in the data (see effectiveYear in BudgetWidget). */
  year: number | null;
  displayUnit: DisplayUnit;
  drillDownType: string | null;
  drillDownSubType: string | null;
  /** Title id only; the label is resolved via useTitleDetails so renames follow. */
  titleId: string | null;
  brokenOutTitleIds: string[];
}

export const EMBED_CHART_KINDS: readonly EmbedChartKind[] = ['area', 'bar'];

export function isEmbedChartKind(v: string): v is EmbedChartKind {
  return (EMBED_CHART_KINDS as readonly string[]).includes(v);
}

// Filter fields that ride the URL under the API's own param names. `query` is
// absent on purpose (see the header comment); `type` is handled separately
// because it is the one field with a non-null default.
const FILTER_PARAMS: ReadonlyArray<[keyof FilterState, string]> = [
  ['einzelplan', 'ep'],
  ['kapitel', 'kap'],
  ['expenditureType', 'expType'],
  ['subExpType', 'subExpType'],
  ['funktion', 'funktion'],
  ['oberfunktion', 'oberfunktion'],
  ['hauptfunktion', 'hauptfunktion'],
  ['gruppierung', 'gruppierung'],
  ['obergruppe', 'obergruppe'],
  ['hauptgruppe', 'hauptgruppe'],
  ['prefix', 'prefix'],
  ['kapitelScope', 'kapitelScope'],
];

// Short, stable aliases for DisplayUnit. The internal names would work too, but
// `unit=gdp` reads better in a snippet than `unit=gdp_percent`.
const UNIT_TO_PARAM: Record<DisplayUnit, string> = {
  absolute: 'abs',
  budget_percent: 'budget',
  gdp_percent: 'gdp',
};
const PARAM_TO_UNIT: Record<string, DisplayUnit> = {
  abs: 'absolute',
  budget: 'budget_percent',
  gdp: 'gdp_percent',
};

export function emptyEmbedFilters(): FilterState {
  return {
    einzelplan: null,
    kapitel: null,
    query: null,
    expenditureType: null,
    subExpType: null,
    funktion: null,
    oberfunktion: null,
    hauptfunktion: null,
    gruppierung: null,
    obergruppe: null,
    hauptgruppe: null,
    prefix: null,
    kapitelScope: null,
    type: 'A',
  };
}

export interface EncodeEmbedInput {
  chart: EmbedChartKind;
  filters: FilterState;
  year: number | null;
  displayUnit: DisplayUnit;
  drillDownType: string | null;
  drillDownSubType: string | null;
  titleId: string | null;
  brokenOutTitleIds: string[];
}

/**
 * Serialise a view into the embed query string (no leading '?').
 * Defaults are omitted so a plain view produces a short, legible URL.
 */
export function encodeEmbedConfig(input: EncodeEmbedInput): string {
  const p = new URLSearchParams();

  // 'A' (Ausgaben) is the default on both client and server, so only 'E' needs saying.
  if (input.filters.type === 'E') p.set('type', 'E');

  for (const [key, param] of FILTER_PARAMS) {
    const value = input.filters[key];
    if (typeof value === 'string' && value !== '') p.set(param, value);
  }

  if (input.year !== null) p.set('year', String(input.year));
  if (input.displayUnit !== 'absolute') p.set('unit', UNIT_TO_PARAM[input.displayUnit]);
  if (input.drillDownType) p.set('drill', input.drillDownType);
  if (input.drillDownSubType) p.set('drillSub', input.drillDownSubType);
  if (input.titleId) p.set('title', input.titleId);
  if (input.brokenOutTitleIds.length > 0) p.set('breakout', input.brokenOutTitleIds.join(','));

  return p.toString();
}

/**
 * Parse an embed query string back into a config.
 *
 * Tolerant by design: unknown params are ignored and an unparseable value falls
 * back to that field's default. An embed on someone else's page must never go
 * blank because a param was mistyped or a future field was dropped.
 */
export function decodeEmbedConfig(search: string, chart: EmbedChartKind): EmbedConfig {
  const p = new URLSearchParams(search);
  const filters = emptyEmbedFilters();

  // A spotlight is a top-level view preset: it resolves onto the clean filter
  // state FIRST, so any explicit param below can still override it. Mirrors
  // handleSpotlightClick in BudgetWidget (clearFilters, then set the one field).
  const spotlightId = p.get('spotlight');
  if (spotlightId) {
    const spotlight = SPOTLIGHTS.find((s) => s.id === spotlightId);
    // filterType values are FilterState keys 1:1 — see Spotlights.tsx.
    if (spotlight) filters[spotlight.filterType] = spotlight.filterValue;
  }

  if (p.get('type') === 'E') filters.type = 'E';

  for (const [key, param] of FILTER_PARAMS) {
    const value = p.get(param);
    if (value !== null && value !== '') {
      // Every filter field except `type` is `string | null`.
      (filters[key] as string | null) = value;
    }
  }

  const yearRaw = p.get('year');
  const yearNum = yearRaw === null ? NaN : Number(yearRaw);
  // Guard the range so a junk year can't push the chart to an empty period.
  const year =
    Number.isInteger(yearNum) && yearNum >= 1949 && yearNum <= 2100 ? yearNum : null;

  const unitRaw = p.get('unit');
  const displayUnit = (unitRaw && PARAM_TO_UNIT[unitRaw]) || 'absolute';

  const breakoutRaw = p.get('breakout');
  const brokenOutTitleIds = breakoutRaw
    ? breakoutRaw.split(',').map((s) => s.trim()).filter(Boolean)
    : [];

  return {
    chart,
    filters,
    year,
    displayUnit,
    drillDownType: p.get('drill') || null,
    drillDownSubType: p.get('drillSub') || null,
    titleId: p.get('title') || null,
    brokenOutTitleIds,
  };
}
